import styled from '@emotion/styled'
import { Form, Formik } from 'formik'
import gql from 'graphql-tag'
import React, { useCallback, useState } from 'react'
import toastr from 'toastr'
import useRecordSubmissionMutation from '../../../../hooks/useRecordSubmissionMutation'
import cloudinaryTools from '../../../../util/cloudinaryTools'
import ResponsiveImage from '../../misc/MediaPresets'
import { OrganizationMembershipsQuery } from './OrganizationsSection'
import { Hugeicon } from '../../../../icons/Hugeicon'

type PendingOrganizationRowProps = {
  currentUserId?: string
  onCancel: () => void
  onCreateSuccess: (org: { id: string; name: string }) => void
}

const ORGANIZATION_TYPE_OPTIONS = [
  { value: '', label: 'Select type...' },
  { value: 'non_profit', label: 'Non-Profit' },
  { value: 'business', label: 'Business' },
]

const ORGANIZATION_CREATE_MUTATION = gql`
  mutation OrganizationCreate($input: OrganizationCreateInput!) {
    organizationCreate(input: $input) {
      organization {
        id
        name
      }
      errors {
        attribute
        message
      }
    }
  }
`

const PendingOrganizationRow: React.FC<PendingOrganizationRowProps> = ({
  onCancel,
  onCreateSuccess,
}) => {
  const [logoPublicId, setLogoPublicId] = useState<string>('')
  const [uploading, setUploading] = useState(false)

  const { handleSubmit, mutationLoading } = useRecordSubmissionMutation({
    query: ORGANIZATION_CREATE_MUTATION,
    queryName: 'organizationCreate',
    recordName: 'organization',
    onSuccess: (record) => {
      toastr.success('Organization created!')
      onCreateSuccess(record as { id: string; name: string })
    },
    submitOptions: { refetchQueries: () => [{ query: OrganizationMembershipsQuery }] },
  })

  const handleFormSubmit = (values: typeof initialValues) => {
    const input = {
      name: values.name,
      organizationType: values.organizationType || null,
      contactEmail: values.contactEmail || null,
      phone: values.phone || null,
      websiteUrl: values.websiteUrl || null,
      streetAddress: values.streetAddress || null,
      city: values.city || null,
      state: values.state || null,
      zip: values.zip || null,
      logoPublicId: logoPublicId || null,
    }
    handleSubmit(input)
  }

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toastr.error('Please select a valid image file.')
      return
    }

    setUploading(true)

    await cloudinaryTools.upload(file, { folder: 'logo' }, (error, response) => {
      setUploading(false)

      if (error) {
        toastr.error(error)
      } else {
        setLogoPublicId((response as { public_id: string }).public_id)
      }
    })
  }, [])

  const handleRemoveLogo = useCallback(() => {
    setLogoPublicId('')
  }, [])

  const initialValues = {
    name: '',
    organizationType: '',
    contactEmail: '',
    phone: '',
    websiteUrl: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <StyledRowLeft>
          <StyledOrgIcon>
            <BuildingIcon />
          </StyledOrgIcon>
          <StyledOrgInfo>
            <StyledOrgName>New Organization</StyledOrgName>
            <StyledMetaRow>
              <StyledNewBadge>NEW</StyledNewBadge>
              <StyledHint>Fill in the details below to create</StyledHint>
            </StyledMetaRow>
          </StyledOrgInfo>
        </StyledRowLeft>
      </StyledHeader>

      <StyledDetailContainer>
        <Formik initialValues={initialValues} onSubmit={handleFormSubmit}>
          {({ values, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <StyledFieldsSection>
                <StyledFieldGroup>
                  <StyledLabel>Organization Name *</StyledLabel>
                  <StyledInput
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter organization name"
                    autoFocus
                  />
                </StyledFieldGroup>

                <StyledFieldGroup>
                  <StyledLabel>Organization Type</StyledLabel>
                  <StyledSelect name="organizationType" value={values.organizationType} onChange={handleChange} onBlur={handleBlur}>
                    {ORGANIZATION_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </StyledSelect>
                </StyledFieldGroup>

                <StyledLogoUploadContainer>
                  <StyledLabel>Organization Logo</StyledLabel>
                  <StyledLogoUploadContent>
                    <StyledLogoPreview>
                      {logoPublicId ? (
                        <ResponsiveImage publicId={logoPublicId} width="80" height="80" />
                      ) : (
                        <StyledPlaceholderLogo>
                          <BuildingIcon />
                        </StyledPlaceholderLogo>
                      )}
                    </StyledLogoPreview>
                    <StyledLogoActions>
                      <StyledUploadLabel htmlFor="pending-logo-upload" isUploading={uploading}>
                        <CameraIcon />
                        {uploading ? 'UPLOADING...' : 'UPLOAD LOGO'}
                        <input id="pending-logo-upload" type="file" accept="image/*" onChange={handleFileChange} disabled={uploading} />
                      </StyledUploadLabel>
                      {logoPublicId && (
                        <StyledRemoveLogoButton type="button" onClick={handleRemoveLogo}>
                          <TrashIcon />
                          REMOVE
                        </StyledRemoveLogoButton>
                      )}
                    </StyledLogoActions>
                  </StyledLogoUploadContent>
                </StyledLogoUploadContainer>

                <StyledFieldGroup>
                  <StyledLabelWithIcon>
                    <LocationIcon />
                    Address
                  </StyledLabelWithIcon>
                  <StyledInput
                    type="text"
                    name="streetAddress"
                    value={values.streetAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Street Address"
                  />
                  <StyledAddressRow>
                    <StyledInputSmall
                      type="text"
                      name="city"
                      value={values.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="City"
                    />
                    <StyledInputSmall
                      type="text"
                      name="state"
                      value={values.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="State"
                    />
                    <StyledInputSmall
                      type="text"
                      name="zip"
                      value={values.zip}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Zip"
                    />
                  </StyledAddressRow>
                </StyledFieldGroup>

                <StyledTwoColumnRow>
                  <StyledFieldGroup>
                    <StyledLabelWithIcon>
                      <GlobeIcon />
                      Website
                    </StyledLabelWithIcon>
                    <StyledInput
                      type="url"
                      name="websiteUrl"
                      value={values.websiteUrl}
                      onChange={handleChange}
                      onBlur={e => {
                        handleBlur(e)
                        const val = values.websiteUrl.trim()
                        if (val && !val.startsWith('http://') && !val.startsWith('https://')) {
                          setFieldValue('websiteUrl', `https://${val}`)
                        }
                      }}
                      placeholder="https://example.com"
                    />
                  </StyledFieldGroup>

                  <StyledFieldGroup>
                    <StyledLabelWithIcon>
                      <PhoneIcon />
                      Phone
                    </StyledLabelWithIcon>
                    <StyledInput
                      type="tel"
                      name="phone"
                      value={values.phone}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '')
                        let formatted = digits
                        if (digits.length > 3 && digits.length <= 6) {
                          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
                        } else if (digits.length > 6) {
                          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
                        }
                        setFieldValue('phone', formatted)
                      }}
                      onBlur={handleBlur}
                      placeholder="(555) 555-5555"
                    />
                  </StyledFieldGroup>
                </StyledTwoColumnRow>

                <StyledFieldGroup>
                  <StyledLabelWithIcon>
                    <EmailIcon />
                    Contact Email
                  </StyledLabelWithIcon>
                  <StyledInput
                    type="email"
                    name="contactEmail"
                    value={values.contactEmail}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="contact@example.com"
                  />
                </StyledFieldGroup>
              </StyledFieldsSection>

              <StyledButtonRow>
                <StyledCancelButton type="button" onClick={onCancel}>
                  CANCEL
                </StyledCancelButton>
                <StyledCreateButton type="submit" disabled={mutationLoading || !values.name.trim()}>
                  {mutationLoading ? 'CREATING...' : 'CREATE ORGANIZATION'}
                </StyledCreateButton>
              </StyledButtonRow>
            </Form>
          )}
        </Formik>
      </StyledDetailContainer>
    </StyledContainer>
  )
}

export default PendingOrganizationRow

// Icons
const BuildingIcon: React.FC = () => <Hugeicon name="bank" size={20} />

const LocationIcon: React.FC = () => <Hugeicon name="location" size={16} />

const GlobeIcon: React.FC = () => <Hugeicon name="globe" size={16} />

const PhoneIcon: React.FC = () => <Hugeicon name="call" size={16} />

const EmailIcon: React.FC = () => <Hugeicon name="mail" size={16} />

const CameraIcon: React.FC = () => <Hugeicon name="camera" size={14} />

const TrashIcon: React.FC = () => <Hugeicon name="delete" size={14} />

// Styled Components
const StyledContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border: 2px solid #a855f7;
  overflow: hidden;
`

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #faf5ff;
`

const StyledRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StyledOrgIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const StyledOrgInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledOrgName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #102638;
`

const StyledMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledNewBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: #a855f7;
  color: white;
`

const StyledHint = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-style: italic;
`

const StyledDetailContainer = styled.div`
  padding: 24px;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 480px) {
    padding: 16px;
  }
`

const StyledFieldsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const StyledFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const StyledLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`

const StyledLabelWithIcon = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`

const StyledInput = styled.input`
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #102638;
  transition: border-color 0.15s ease-out;

  &:focus {
    outline: none;
    border-color: #a855f7;
  }

  &::placeholder {
    color: #9ca3af;
  }
`

const StyledInputSmall = styled(StyledInput)`
  flex: 1;
  min-width: 0;
`

const StyledSelect = styled.select`
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #102638;
  cursor: pointer;
  transition: border-color 0.15s ease-out;

  &:focus {
    outline: none;
    border-color: #a855f7;
  }
`

const StyledAddressRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`

const StyledTwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const StyledButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`

const StyledCancelButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 14px 24px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.15s ease-out;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`

const StyledCreateButton = styled.button`
  background: #a855f7;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 14px 32px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background-color 0.15s ease-out;

  &:hover:not(:disabled) {
    background: #9333ea;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`

const StyledLogoUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledLogoUploadContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StyledLogoPreview = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  background: #f9fafb;
  border: 1px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledPlaceholderLogo = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
  color: white;
`

const StyledLogoActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledUploadLabel = styled.label<{ isUploading: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #1e3a5f;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: ${props => (props.isUploading ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.isUploading ? 0.7 : 1)};
  transition: background-color 0.15s ease-out;

  &:hover {
    background: ${props => (props.isUploading ? '#1e3a5f' : '#2d4a6f')};
  }

  input {
    display: none;
  }
`

const StyledRemoveLogoButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: #dc2626;
  border: 1px solid #dc2626;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.15s ease-out;

  &:hover {
    background: #fef2f2;
  }
`
